import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';

import { ImmutableParagraphExerciseEntity } from '..';
import { CoreLink, Icon, LinePart } from '../../../components';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import { WEBFORM_ID_GOAL } from '../../../config/constants';

import './style.scss';

interface Props extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableParagraphExerciseEntity;
  exerciseStatus?: 'done' | 'lastExerciseDone' | 'start' | 'end' | 'locked' | 'available';
}

const ParagraphExercise: React.FC<Props> = ({
  entity,
  className,
  exerciseStatus,
}) => {
  const { t } = useTranslation();
  const { recoveryPlan } = useRoutes();

  const title = entity.getIn(['field_title', 0, 'value'], '');
  const time = entity.getIn(['field_time', 0, 'value'], null);
  const iconType = entity.getIn(['field_icon', 0, 'value'], '');
  const webformUrl = (entity.getIn(['field_webform', 0, 'url'], ''));
  const webformId = (entity.getIn(['field_webform', 0, 'target_id'], ''));

  const { buildClass, joinClasses } = useClasses('ParagraphExercise', exerciseStatus);

  // prevent users from editing the goal exercise because that will reorganize the entire
  // programme. Instead, clicking on the goal exercise, after completion, will link users
  // to the recovery plan page.
  const link =
  webformId === WEBFORM_ID_GOAL && (exerciseStatus === 'done' || exerciseStatus === 'lastExerciseDone')
    ? recoveryPlan
    : webformUrl;

  const disabled = exerciseStatus === 'locked';
  const containerClasses = c('ParagraphExercise__container', { disabled });

  const linkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      e.preventDefault();
    }
  };

  return (
    <div className={containerClasses}>
      <div className={joinClasses(buildClass(), className)}>
        <LinePart exerciseStatus={exerciseStatus} />
        <div className={buildClass('icon-container')}>
          <div className={buildClass('icon-container__inner')}>
            <Icon icon={iconType} variant={exerciseStatus === 'locked' ? '3' : '4'} />
          </div>
        </div>
        <div className={buildClass('title-container')}>
          <CoreLink
            aria-disabled={disabled}
            href={link}
            onClick={linkClick}
            className={buildClass('link')}
          >
            <h3 className={joinClasses(buildClass('title'), 'asH4')}>{title}</h3>
          </CoreLink>
          {time && (
            <p className={joinClasses(buildClass('time'), 'body--tiny')}>
              {t('paragraphs.exercise.time', { time })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParagraphExercise;
